import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import SafariExperiencesCarousel from '../../components/SafariExperiencesCarousel'
import FAQSection from '../../components/FAQSection'
import { safariExperiences } from '../../data/safariExperiences'

export default function UgandaSafari17Days() {
    const [isModalOpen, setIsModalOpen] = useState(false)

    const faqs = [
        {
            question: "Is this safari suitable for families with children?",
            answer: "Most of the safari is family-friendly! However, gorilla trekking has a minimum age of 15 years, and chimpanzee tracking is usually 12+. For families with younger children, we can substitute alternative activities like nature walks, cultural visits, or relaxation time at the lodges. The safari vehicle travel between parks can be long, so consider your children's tolerance for car journeys."
        },
        {
            question: "How fit do I need to be for this safari?",
            answer: "Moderate fitness is recommended. The gorilla trek can be challenging (2-8 hours over steep, muddy terrain), and the walking safari requires basic mobility. However, our guides can adjust the pace, and in Bwindi, rangers assign you to gorilla families based on your fitness level. The rest of the safari involves game drives where you remain in the vehicle, which require no physical exertion."
        },
        {
            question: "What vaccinations do I need?",
            answer: "Yellow fever vaccination is mandatory for entry into Uganda (you'll need to show your certificate). Highly recommended vaccinations include hepatitis A and B, typhoid, and tetanus. Malaria prophylaxis is strongly advised as most parks are in malaria-endemic zones. Consult your travel health clinic 6-8 weeks before departure for personalized medical advice."
        },
        {
            question: "When should I book my gorilla permits?",
            answer: "Book as early as possible—ideally 3-6 months in advance, especially for travel during peak season (June-September, December-February). Permits are limited to protect the gorillas, and popular months sell out quickly. We can secure your permits once you've committed to your travel dates."
        },
        {
            question: "Can I customize this itinerary?",
            answer: "Absolutely! This itinerary is a comprehensive template, but every element is flexible. Want to add more days in Bwindi? Prefer luxury tented camps over lodges? Interested in adding Kidepo Valley or Mgahinga? We'll work with you to design the perfect safari matching your interests, budget, and available time."
        }
    ]

    const itinerary = [
        {
            day: 1,
            title: "Arrival in Entebbe",
            description: "Upon your arrival at Entebbe International Airport, you will be warmly greeted by your safari guide who will transfer you to your hotel. Depending on your arrival time, we might explore the Entebbe Botanical Gardens or visit the Uganda Wildlife Education Centre together. You will rest and prepare for your incredible adventure ahead.",
            highlights: ["Airport meet and greet", "Hotel check-in", "Optional botanical gardens visit"]
        },
        {
            day: 2,
            title: "Transfer to Murchison Falls National Park",
            description: "After breakfast, we'll begin your scenic journey north toward Murchison Falls National Park. Along the way, we'll stop at Ziwa Rhino Sanctuary where you will have an unforgettable on-foot rhino tracking experience. We'll continue to the park, arriving in time for an evening game drive before you settle into your lodge.",
            highlights: ["Ziwa rhino tracking", "Scenic drive through Ugandan countryside", "Evening game drive"]
        },
        {
            day: 3,
            title: "Murchison Falls Game Drive & Boat Safari",
            description: "We'll start your day with an early morning game drive across the northern bank of the Nile, searching for lions, elephants, giraffes, and leopards. You will return for lunch. In the afternoon, we'll take a thrilling boat cruise to the base of Murchison Falls, where you will witness hippos, crocodiles, and abundant birdlife. If you wish, we can arrange a hike to the top of the falls for spectacular views.",
            highlights: ["Morning game drive", "Boat cruise to Murchison Falls", "Hike to top of falls (optional)"]
        },
        {
            day: 4,
            title: "Travel to Kibale Forest National Park",
            description: "We'll depart Murchison Falls and journey south to Kibale Forest National Park, renowned as the 'Primate Capital of the World.' The drive offers stunning views of the Albertine Escarpment and local villages. You will arrive at your lodge near Kibale in the late afternoon.",
            highlights: ["Scenic drive", "Views of Albertine Escarpment", "Local village sightings"]
        },
        {
            day: 5,
            title: "Chimpanzee Tracking & Bigodi Wetland Sanctuary",
            description: "After an early briefing, you will venture into Kibale Forest to track chimpanzees - spending an unforgettable hour observing these intelligent primates in their natural habitat. In the afternoon, we'll explore the Bigodi Wetland Sanctuary, a community-run paradise for birdwatchers where you will spot over 138 bird species and 8 primate species.",
            highlights: ["Chimpanzee tracking in Kibale", "Bigodi Wetland walk", "Primate and bird spotting"]
        },
        {
            day: 6,
            title: "Transfer to Queen Elizabeth National Park & Evening Game Drive",
            description: "We'll travel to Queen Elizabeth National Park, Uganda's most diverse protected area. The park's ecosystems range from savannah and wetlands to forests and crater lakes. Upon arrival, you will check in before we embark on an evening game drive to spot elephants, buffaloes, and antelopes before they settle for the night.",
            highlights: ["Transfer to Queen Elizabeth", "Evening game drive", "Crater lake views"]
        },
        {
            day: 7,
            title: "Queen Elizabeth - Game Drive & Kazinga Channel Cruise",
            description: "We'll begin with a morning game drive in the Kasenyi plains, prime territory for lion sightings. You will return for lunch. In the afternoon, we'll enjoy a relaxing boat cruise along the Kazinga Channel where you will observe the enormous concentration of hippos and Nile crocodiles, plus spectacular birdlife including African fish eagles.",
            highlights: ["Morning game drive", "Kazinga Channel boat cruise", "Birdwatching paradise"]
        },
        {
            day: 8,
            title: "Ishasha Tree-Climbing Lions & Transfer to Bwindi",
            description: "We'll head to the Ishasha sector, renowned for its unique tree-climbing lions lounging in fig trees. You will watch for these magnificent predators. After this special sighting, we'll continue to Bwindi Impenetrable National Park, a UNESCO World Heritage Site. You will enjoy the drive that showcases breathtaking highland scenery and terraced hills.",
            highlights: ["Tree-climbing lions in Ishasha", "Scenic highland drive", "Arrive at Bwindi"]
        },
        {
            day: 9,
            title: "Gorilla Trekking in Bwindi Impenetrable Forest",
            description: "Today is the pinnacle of your safari! After a briefing at park headquarters, you will trek into the ancient rainforest to find a habituated gorilla family. You will spend one magical hour sitting mere meters from these gentle giants, observing their family interactions. The trekking duration varies (2-8 hours) depending on gorilla movements.",
            highlights: ["Gorilla trekking - 1 hour with gorillas", "Ancient rainforest exploration", "Professional ranger guides"]
        },
        {
            day: 10,
            title: "Bwindi Relaxation or Batwa Cultural Experience",
            description: "You will enjoy a relaxed morning at your lodge, or if you wish, we can arrange a cultural walk to meet the Batwa people, the forest's original inhabitants. You will learn about their traditional hunting techniques, medicinal plants, and forest survival skills. In the afternoon, we'll transfer to the stunning Lake Bunyonyi.",
            highlights: ["Batwa cultural experience (optional)", "Transfer to Lake Bunyonyi", "Relaxation time"]
        },
        {
            day: 11,
            title: "Lake Bunyonyi Activities",
            description: "You will spend your day at Africa's second-deepest lake, dotted with 29 scenic islands. You can choose from canoeing, island hopping, visiting local communities, or hiking to panoramic viewpoints. The lake's tranquil beauty provides perfect reflection time after your gorilla encounter.",
            highlights: ["Canoeing on Lake Bunyonyi", "Island hopping", "Community visits"]
        },
        {
            day: 12,
            title: "Travel to Lake Mburo National Park",
            description: "We'll journey to Lake Mburo National Park, Uganda's smallest savannah park but rich in biodiversity. The drive takes you through pastoral landscapes with long-horned Ankole cattle. You will arrive in time for an optional evening game drive or boat trip on Lake Mburo.",
            highlights: ["Scenic drive", "Ankole cattle sightings", "Evening activities"]
        },
        {
            day: 13,
            title: "Walking Safari & Boat Trip in Lake Mburo",
            description: "You will experience Uganda's only national park where walking safaris are permitted! You will trek on foot accompanied by an armed ranger, getting close to zebras, impalas, and antelopes. Later, we'll enjoy a boat trip on Lake Mburo where you will see hippos, crocodiles, and water birds including the rare African finfoot.",
            highlights: ["Walking safari", "Boat trip on Lake Mburo", "Close wildlife encounters"]
        },
        {
            day: 14,
            title: "Morning Game Drive & Transfer to Entebbe",
            description: "We'll enjoy a final morning game drive in Lake Mburo, then begin your journey back to Entebbe. We'll stop at the Equator monument where you will have time for photos and souvenir shopping, marking your symbolic crossing between hemispheres. You will arrive in Entebbe for overnight.",
            highlights: ["Final game drive", "Equator monument visit", "Return to Entebbe"]
        },
        {
            day: 15,
            title: "Optional Ngamba Island Chimpanzee Sanctuary",
            description: "Depending on your departure schedule, we can visit Ngamba Island Chimpanzee Sanctuary on Lake Victoria, home to orphaned and rescued chimpanzees. Alternatively, you can relax at your hotel or we can explore Entebbe's craft markets before your evening activities.",
            highlights: ["Ngamba Island visit (optional)", "Relaxation time", "Craft market shopping"]
        },
        {
            day: 16,
            title: "Jinja & Source of the Nile Adventure",
            description: "We'll take a day trip to Jinja, Uganda's adventure capital. You will visit the Source of the Nile where the world's longest river begins its journey. For thrill-seekers, we can arrange optional activities including white-water rafting, bungee jumping, or kayaking. We'll return to Entebbe in the evening.",
            highlights: ["Source of the Nile visit", "Optional adventure activities", "Jinja exploration"]
        },
        {
            day: 17,
            title: "Departure",
            description: "Enjoy a leisurely breakfast before your transfer to Entebbe International Airport for your departure flight. Depending on your schedule, you might have time for last-minute souvenir shopping or a final stroll along Lake Victoria's shores.",
            highlights: ["Breakfast", "Airport transfer", "Departure"]
        }
    ]

    return (
        <div>
            {/* Hero Section */}
            <section
                className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
                style={{
                    backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(/images/packages/17-days-safari.jpg)',
                }}
            >
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl text-white">
                        <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
                            17 Days Ultimate Uganda Safari – 2025/2026
                        </h1>
                        <p className="text-xl md:text-2xl mb-6">
                            The complete Uganda experience: Gorillas, chimps, wildlife, culture & adventure
                        </p>
                        <div className="flex items-center gap-6 mb-6">
                            <div className="flex items-center gap-2">
                                <div className="flex text-safari-gold">
                                    {[...Array(5)].map((_, i) => (
                                        <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                                    ))}
                                </div>
                                <span className="text-lg">(250+ reviews)</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 mb-8">
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                                UWA Licensed
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-leaf" className="w-4 h-4 mr-2" />
                                Eco-Certified
                            </div>
                            <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                                <FontAwesomeIcon icon="fa-heart" className="w-4 h-4 mr-2" />
                                Community Supported
                            </div>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            Request Customized Itinerary
                        </button>
                    </div>
                </div>
            </section>

            {/* Page Content */}
            <div className="container mx-auto px-4 py-16">
                <div className="lg:grid lg:grid-cols-3 lg:gap-12">
                    {/* Main Content */}
                    <main className="lg:col-span-2">
                        {/* Full Safari Description */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                                Your Ultimate Uganda Adventure
                            </h2>

                            <div className="prose prose-lg max-w-none">
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    Imagine spending 17 days discovering everything that makes Uganda the "Pearl of Africa." This isn't just a safari—it's a complete immersion into one of Africa's most biodiverse nations, where you'll encounter mountain gorillas in misty rainforests, track chimpanzees through dense jungle, watch lions lounging in trees, and witness the raw power of Murchison Falls as the Nile explodes through a narrow gorge.
                                </p>

                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    This comprehensive itinerary takes you through Uganda's most iconic national parks and hidden gems. You'll visit Murchison Falls, Kibale Forest, Queen Elizabeth, Bwindi Impenetrable, Lake Bunyonyi, and Lake Mburo—each offering unique ecosystems and wildlife experiences. With optional extensions to Ngamba Island and Jinja's adventure activities, this is your chance to experience the full spectrum of Ugandan adventures.
                                </p>

                                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Why This Safari Stands Out</h3>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    What makes this 17-day safari special is its perfect balance of wildlife encounters, cultural experiences, and relaxation. You're not just racing from park to park—you have time to truly absorb each destination. Spend an hour with a gorilla family watching infants play while the silverback keeps watch. Track chimpanzees as they hoot and swing through Kibale's canopy. Watch tree-climbing lions in Ishasha, a behavior found in only two places on Earth.
                                </p>

                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    The itinerary also includes meaningful cultural exchanges. Meet the Batwa people, the forest's original inhabitants, and learn survival skills passed down through generations. Visit local communities around Bigodi Wetland. Witness traditional Ugandan life in villages along your route. These authentic interactions provide context and connection that pure wildlife viewing cannot.
                                </p>

                                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Best Time to Experience This Safari</h3>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    The dry seasons (June-September and December-February) offer the best conditions for this safari. Trails are drier and more accessible for gorilla trekking, wildlife congregates around water sources making game viewing exceptional, and roads between parks are in better condition. That said, Uganda's temperate climate means this safari is enjoyable year-round, with the wet season offering lush landscapes and excellent birdwatching.
                                </p>
                            </div>
                        </section>

                        {/* Safari Highlights */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                                Safari Highlights
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-paw" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Mountain Gorilla Encounter</h3>
                                        <p className="text-gray-700">Spend an unforgettable hour with mountain gorillas in Bwindi's ancient rainforest</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-tree" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Chimpanzee Tracking</h3>
                                        <p className="text-gray-700">Track habituated chimpanzee communities in the Primate Capital of the World</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-water" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Murchison Falls Power</h3>
                                        <p className="text-gray-700">Witness the Nile's explosive force as it thunders through a 7-meter gorge</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-lion" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Tree-Climbing Lions</h3>
                                        <p className="text-gray-700">See the rare behavior of lions lounging in fig trees in Ishasha</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-person-walking" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Walking Safari</h3>
                                        <p className="text-gray-700">Experience close wildlife encounters on foot in Lake Mburo National Park</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                                            <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-safari-green mb-2">Cultural Encounters</h3>
                                        <p className="text-gray-700">Meet the Batwa people and learn their ancient forest survival skills</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Day-by-Day Itinerary */}
                        <section className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                                Your 17-Day Itinerary
                            </h2>
                            <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                                <p className="text-gray-700 text-lg">
                                    <strong>📋 Tentative Itinerary:</strong> This itinerary is flexible and can be adjusted based on your travel preferences, budget, and specific interests. We customize every safari to match your unique needs!
                                </p>
                            </div>

                            <div className="space-y-6">
                                {itinerary.map((day) => (
                                    <div key={day.day} className="card">
                                        <div className="p-6">
                                            <div className="flex gap-4">
                                                <div className="flex-shrink-0">
                                                    <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                                                        {day.day}
                                                    </div>
                                                </div>
                                                <div className="flex-grow">
                                                    <h3 className="text-xl font-bold text-safari-green mb-2">{day.title}</h3>
                                                    <p className="text-gray-700 mb-4">{day.description}</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {day.highlights.map((highlight, index) => (
                                                            <span
                                                                key={index}
                                                                className="bg-safari-light text-safari-green px-3 py-1 rounded-full text-sm"
                                                            >
                                                                {highlight}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* What's Included/Excluded */}
                        <section className="mb-16">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="card p-6">
                                    <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                                        What's Typically Included
                                    </h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">All accommodation (lodges/camps)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">4x4 safari vehicle with professional driver-guide</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Gorilla trekking permit ($800 value)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Chimpanzee tracking permit</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">All park entry fees and game drives</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Boat cruises (Kazinga Channel, Murchison Falls, Lake Mburo)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Meals as specified (usually full board)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Bottled drinking water during safari</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="card p-6">
                                    <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                                        <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                                        What's Not Included
                                    </h3>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">International flights</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Uganda visa ($50)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Travel insurance (highly recommended)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Tips for guides and lodge staff</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Personal expenses and souvenirs</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Optional activities (white-water rafting, bungee jumping)</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Alcoholic beverages</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                                            <span className="text-gray-700">Porter hire for gorilla trekking (optional but recommended)</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* FAQs */}
                        <FAQSection faqs={faqs} />

                        {/* Related Safari Experiences Carousel */}
                        <section>
                            <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-4 text-center">
                                Explore More Safari Experiences
                            </h2>
                            <p className="text-center text-gray-700 mb-12 text-lg">
                                Discover other carefully curated safari adventures across Uganda
                            </p>

                            <SafariExperiencesCarousel
                                experiences={safariExperiences}
                                currentExperience="17-days-ultimate-safari"
                            />
                        </section>
                    </main>

                    {/* Sidebar */}
                    <aside className="lg:col-span-1">
                        <div className="sticky top-24">
                            <div className="card p-6 mb-8">
                                <h3 className="text-2xl font-bold text-safari-green mb-4">Safari Details</h3>
                                <div className="space-y-4 text-gray-700">
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Duration:</strong> 17 Days / 16 Nights</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Best Time:</strong> Jun-Sep, Dec-Feb</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-map-marked-alt" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Parks Visited:</strong> 6 National Parks</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Difficulty:</strong> Moderate</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Group Size:</strong> Private or small group</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon="fa-home" className="w-6 h-6 text-safari-green" />
                                        <span><strong>Accommodation:</strong> Lodges/Camps (all levels)</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                                >
                                    Get Custom Quote
                                </button>

                                <div className="bg-safari-green/5 border border-safari-green/20 rounded-lg p-4 mt-4">
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        <span className="font-bold text-safari-green">🌿 Travel with Purpose:</span> Part of your booking contributes to our <Link to="/planting-green-paths" className="text-safari-green font-semibold hover:underline">'Planting Green Paths'</Link> tree planting initiative, helping restore Uganda's ecosystems by securing a tree, planting and tree growth follow up. You can choose to participate in planting a tree activity during your safari or simply support the cause.
                                    </p>
                                </div>
                            </div>

                            <div className="card p-6 mb-8">
                                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-lightbulb" className="w-6 h-6 text-safari-gold" />
                                    Packing Essentials
                                </h3>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Sturdy, waterproof hiking boots</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Long pants and long sleeves for trekking</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Rain jacket (waterproof)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Gardening gloves for grabbing vines</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Binoculars for wildlife viewing</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Camera with zoom lens</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Sunscreen, hat, and sunglasses</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-green mt-0.5" />
                                        <span>Insect repellent</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="card p-6">
                                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                                    <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                                    Health & Safety
                                </h3>
                                <ul className="space-y-3 text-gray-700 text-sm">
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Yellow fever vaccination mandatory</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Malaria prophylaxis highly recommended</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                                        <span>Travel insurance with evacuation coverage advised</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <BookingModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                experience="17 Days Ultimate Uganda Safari"
            />
        </div>
    )
}
